import { TPrivateListContentState } from '@/components/atoms/PrivateListContainer/PrivateListContainer';
import { TLangIso, TLocale } from '@/types/global';
import { create,  } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface IExecutedAnimations {
  homepage: boolean;
  recentProjectsGrid: boolean;
}

export interface PageState {
  isUserLogged: boolean;
  locale: TLocale;
  langIso: TLangIso;
  projectBreadcrumbPath: string | null;
  isPreviousPageWork: boolean;
  gsapTimeout: number;
  isMobileMenuOpen: boolean;
  isSmootherReady: boolean;
  isScrollBlocked: boolean;
  usePrefersReducedMotion: boolean;
  isPageLoadFromMobileHeader: boolean;
  heightWindowUnit: number;
  pageTransitionDuration: number;
  defaultPageTransitionDuration: number;
  isPageTransitioning: boolean;
  isImagesGalleryDisplayed: boolean;
  imageFromGalleryToDisplay: string | undefined;
  showNavigationModalAlert: boolean;
  urlOnHoldFromPresentation: string | undefined;
  currentSlugTranslations: {
    en: string;
    es: string;
  };
  shouldDisplayFooter: boolean;
  scrollPosition: number | undefined;
  executedAnimations: IExecutedAnimations;
  isPresentationMode: boolean;
  previousVisitedPath: string | undefined;
  presentationAllowedState: TPrivateListContentState | undefined;
  previousPopPath: string | undefined;
  // numMenuMobileToggles: number;
  lastProjectOriginPath: string | undefined;
  isLastPopBackwards: boolean;
  setIsUserLogged: (value: boolean) => void;
  setLocale: (value: TLocale) => void;
  setLangIso: (value: TLangIso) => void;
  setProjectBreadcrumbPath: (value: string | null) => void;
  setIsPreviousPageWork: (value: boolean) => void;
  setGsapTimeout: (value: number) => void;
  setIsMobileMenuOpen: (value: boolean) => void;
  setIsSmootherReady: (value: boolean) => void;
  setIsScrollBlocked: (value: boolean) => void;
  setUserPrefersReducedMotion: (value: boolean) => void;
  setIsPageLoadFromMobileHeader: (value: boolean) => void;
  setHeightWindowUnit: (value: number) => void;
  setIsPageTransitioning: (value: boolean) => void;
  setPageTransitionDuration: (value: number) => void;
  setIsImagesGalleryDisplayed: (value: boolean) => void;
  setShowNavigationModalAlert: (value: boolean) => void;
  setUrlOnHoldFromPresentation: (value: string | undefined) => void;
  setImageFromGalleryToDisplay: (value: string | undefined) => void;
  setCurrentSlugTranslations: (value: { en: string; es: string }) => void;
  setShouldDisplayFooter: (value: boolean) => void;
  setScrollPosition: (value: number | undefined) => void;
  setExecutedAnimations: (value: IExecutedAnimations) => void;
  setIsPresentationMode: (value: boolean) => void;
  setPresentationAllowedState: (value: TPrivateListContentState | undefined) => void;
  setLoadComesFromBackPopState: (value: boolean) => void;
  // setNumMenuMobileToggles: (value: number) => void;
  setLastProjectOriginPath: (value: string | undefined) => void;
  setPreviousPopPath: (value: string | undefined) => void;
  setIsLastPopBackwards: (value: boolean) => void;
}


export const usePageStore = create<PageState>()(
  devtools(
    (set) => ({
      isUserLogged: false,
      locale: 'en',
      langIso: 'en-US',
      projectBreadcrumbPath: null,
      isPreviousPageWork: false,
      gsapTimeout: 0,
      isMobileMenuOpen: false,
      isSmootherReady: false,
      isScrollBlocked: false,
      usePrefersReducedMotion: false,
      isPageLoadFromMobileHeader: false,
      heightWindowUnit: 0,
      pageTransitionDuration: 150,
      isPageTransitioning: false,
      defaultPageTransitionDuration: 200,
      isImagesGalleryDisplayed: false,
      imageFromGalleryToDisplay: undefined,
      showNavigationModalAlert: false,
      urlOnHoldFromPresentation: undefined,
      currentSlugTranslations: {
        en: '',
        es: '/es',
      },
      shouldDisplayFooter: true,
      scrollPosition: undefined,
      executedAnimations: {
        homepage: false,
        recentProjectsGrid: false,
      },
      isPresentationMode: false,
      presentationAllowedState: undefined,
      projectsNavigation: undefined,
      currentProjectNextPrevious: undefined,
      loadComesFromPopState: false,
      lastProjectOriginPath: '',
      previousPopPath: '',
      isLastPopBackwards: false,
      // numMenuMobileToggles: 0,
      setIsUserLogged: (value) => set({ isUserLogged: value }, false, 'setIsUserLogged'),
      setLocale: (value) => set({ locale: value }, false, 'setLocale'),
      setLangIso: (value) => set({ langIso: value }, false, 'setLangIso'),
      setProjectBreadcrumbPath: (value) => set({ projectBreadcrumbPath: value }, false, 'setProjectBreadcrumbPath'),
      setIsPreviousPageWork: (value) => set({ isPreviousPageWork: value }, false, 'setIsPreviousPageWork'),
      setGsapTimeout: (value) => set({ gsapTimeout: value }, false, 'setGsapTimeout'),
      setIsMobileMenuOpen: (value) => set({ isMobileMenuOpen: value }, false, 'setIsMobileMenuOpen'),
      setIsSmootherReady: (value) => set({ isSmootherReady: value }, false, 'setIsSmootherReady'),
      setIsScrollBlocked: (value) => set({ isScrollBlocked: value }, false, 'setIsScrollBlocked'),
      setUserPrefersReducedMotion: (value) => set({ usePrefersReducedMotion: value }, false, 'setUserPrefersReducedMotion'),
      setIsPageLoadFromMobileHeader: (value) => set({ isPageLoadFromMobileHeader: value }, false, 'setIsPageLoadFromMobileHeader'),
      setHeightWindowUnit: (value) => set({ heightWindowUnit: value }, false, 'setHeightWindowUnit'),
      setIsPageTransitioning: (value) => set({ isPageTransitioning: value }, false, 'setIsPageTransitioning'),
      setPageTransitionDuration: (value) => set({ pageTransitionDuration: value }, false, 'setPageTransitionDuration'),
      setIsImagesGalleryDisplayed: (value) => set({ isImagesGalleryDisplayed: value }, false, 'setIsImagesGalleryDisplayed'),
      setShowNavigationModalAlert: (value) => set({ showNavigationModalAlert: value }, false, 'setShowNavigationModalAlert'),
      setUrlOnHoldFromPresentation: (value) => set({ urlOnHoldFromPresentation: value }, false, 'setUrlOnHoldFromPresentation'),
      setImageFromGalleryToDisplay: (value) => set({ imageFromGalleryToDisplay: value }, false, 'setImageFromGalleryToDisplay'),
      setCurrentSlugTranslations: (value) => set({ currentSlugTranslations: value }, false, 'setCurrentSlugTranslations'),
      setShouldDisplayFooter: (value) => set({ shouldDisplayFooter: value }, false, 'setShouldDisplayFooter'),
      setScrollPosition: (value) => set({ scrollPosition: value }, false, 'setScrollPosition'),
      setExecutedAnimations: (value: IExecutedAnimations) => set({ executedAnimations: value }, false, 'setExecutedAnimations'),
      setIsPresentationMode: (value) => set({ isPresentationMode: value }, false, 'setIsPresentationMode'),
      setPresentationAllowedState: (value) => set({ presentationAllowedState: value }, false, 'setPresentationAllowedState'),
      setLastProjectOriginPath: (value: string | undefined) => set({ lastProjectOriginPath: value }, false, 'setLastProjectOriginPath'),
      setPreviousPopPath: (value: string | undefined) => set({ previousPopPath: value }, false, 'setPreviousPopPath'),
      setIsLastPopBackwards: (value: boolean) => set({ isLastPopBackwards: value }, false, 'setIsLastPopBackwards'),

      // setNumMenuMobileToggles: (value) => set({ numMenuMobileToggles: value }, false, 'setNumMenuMobileToggles'),
    }),
    { name: 'PageStore' } // name shown in devtools
  )
);
